import { Clock } from 'lucide-react'
import { Badge } from '#app/ui/components'

export type EventStatus = `upcoming` | `ongoing` | `past`

type EventOccurrence = {
	startDate: Date | string
	endDate: Date | string
}

type EventStatusBadgeProps = {
	timeStatus: EventStatus
	occurrence: EventOccurrence | null
}

/**
 * Calculates a compact relative time string from an occurrence
 * For upcoming events: "in 4h", "in 2d", "in 3w"
 * For ongoing events: "4h left", "2d left"
 * For past events: "4h ago", "2d ago"
 */
function getCompactTimeDescription(
	occurrence: EventOccurrence,
	timeStatus: EventStatus,
): string {
	const now = new Date()
	const startDate = new Date(occurrence.startDate)
	const endDate = new Date(occurrence.endDate)

	if (timeStatus === `upcoming`) {
		const timeDiff = startDate.getTime() - now.getTime()
		const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
		const hours = Math.floor(
			(timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
		)
		const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))

		if (days >= 7) {
			const weeks = Math.floor(days / 7)
			return `in ${weeks}w`
		} else if (days > 0) {
			return `in ${days}d`
		} else if (hours > 0) {
			return `in ${hours}h`
		} else if (minutes > 0) {
			return `in ${minutes}m`
		} else {
			return `now`
		}
	} else if (timeStatus === `ongoing`) {
		const timeDiff = endDate.getTime() - now.getTime()
		const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
		const hours = Math.floor(
			(timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
		)
		const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))

		if (days > 0) {
			return `${days}d left`
		} else if (hours > 0) {
			return `${hours}h left`
		} else if (minutes > 0) {
			return `${minutes}m left`
		} else {
			return `ending`
		}
	} else {
		// past
		const timeDiff = now.getTime() - endDate.getTime()
		const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
		const hours = Math.floor(
			(timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
		)

		if (days >= 7) {
			const weeks = Math.floor(days / 7)
			return `${weeks}w ago`
		} else if (days > 0) {
			return `${days}d ago`
		} else if (hours > 0) {
			return `${hours}h ago`
		} else {
			return `recently`
		}
	}
}

/**
 * EventStatusBadge component
 *
 * Displays a status badge for an event with integrated relative time.
 * Replaces the getStatusBadge() function with a reusable component.
 *
 * Features:
 * - Shows "Starting in 4h" instead of "Starting Soon" + "in 4 hours"
 * - Compact time format (4h, 2d, 3w instead of "4 hours", "2 days")
 * - Gradient backgrounds for visual hierarchy
 * - Pulsing indicator for live events
 */
export function EventStatusBadge({
	timeStatus,
	occurrence,
}: EventStatusBadgeProps) {
	if (!occurrence) {
		return (
			<Badge variant="secondary" className="shadow-sm">
				No upcoming occurrences
			</Badge>
		)
	}

	const timeDescription = getCompactTimeDescription(occurrence, timeStatus)

	switch (timeStatus) {
		case `upcoming`: {
			// Calculate days until start for color variation
			const now = new Date()
			const startDate = new Date(occurrence.startDate)
			const timeDiff = startDate.getTime() - now.getTime()
			const daysUntilStart = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))

			// Starting very soon (within 1 day) - use orange/red urgency colors
			if (daysUntilStart <= 1) {
				return (
					<Badge
						variant="default"
						className="border border-orange-200 bg-gradient-to-r from-orange-50 to-red-50 text-orange-700 shadow-sm"
					>
						<Clock className="mr-1 h-3 w-3" />
						Starting {timeDescription}
					</Badge>
				)
			}

			// Coming up soon (within 1 week) - use blue colors
			if (daysUntilStart <= 7) {
				return (
					<Badge
						variant="default"
						className="border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm"
					>
						<Clock className="mr-1 h-3 w-3" />
						Starting {timeDescription}
					</Badge>
				)
			}

			// Further out - use standard blue
			return (
				<Badge
					variant="default"
					className="border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm"
				>
					<Clock className="mr-1 h-3 w-3" />
					Starts {timeDescription}
				</Badge>
			)
		}

		case `ongoing`:
			return (
				<Badge
					variant="default"
					className="border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 shadow-sm"
				>
					<div className="mr-1 h-2 w-2 animate-pulse rounded-full bg-green-400" />
					Live Now · {timeDescription}
				</Badge>
			)

		case `past`:
			return (
				<Badge
					variant="secondary"
					className="border border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50 text-gray-600 shadow-sm"
				>
					Ended {timeDescription}
				</Badge>
			)

		default:
			return (
				<Badge variant="secondary" className="shadow-sm">
					Unknown
				</Badge>
			)
	}
}
